import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Toast — Citadel Light Authority toast primitive + provider.
 *
 * Today only MembersScreen has any toast notion (.members-toast in
 * MembersScreen.css). Every other successful create/update/delete in
 * the consultant UI is silent. This provider + hook fixes that with
 * a single, themed toast stack used app-wide.
 *
 * Each toast type maps to a Citadel severity-token left border:
 *   success → Medium green (#2d7a42)
 *   error   → High orange (#c44a1a) — we don't use red because there
 *             is no Critical tier in the 4-tier severity scale.
 *   info    → Info gray (#4a5568)
 *   warn    → Low blue (#1a5a8b) — closest analogue (no dedicated warn)
 *
 * The stack lives in a portal at document.body bottom-right, mirroring
 * MembersScreen's `.members-toast` position so the user's existing
 * mental model survives. Up to 5 visible at a time; auto-dismiss 2.5s
 * by default; click-to-dismiss always available.
 */

export type ToastType = 'success' | 'error' | 'info' | 'warn';

export interface ToastItem {
  id: number;
  type: ToastType;
  message: string;
  /** Auto-dismiss timeout, ms. 0 disables auto-dismiss. */
  durationMs: number;
}

interface ToastContextValue {
  push: (message: string, type?: ToastType, durationMs?: number) => void;
  pushSuccess: (message: string) => void;
  pushError: (message: string) => void;
  dismiss: (id: number) => void;
}

export const ToastContext = createContext<ToastContextValue | null>(null);

const MAX_VISIBLE = 5;
const DEFAULT_DURATION = 2500;

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextIdRef = useRef(1);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, type: ToastType = 'info', durationMs: number = DEFAULT_DURATION) => {
      const id = nextIdRef.current++;
      const item: ToastItem = { id, type, message, durationMs };
      setToasts((current) => {
        const next = [...current, item];
        // Keep at most MAX_VISIBLE; oldest drops off the top.
        if (next.length > MAX_VISIBLE) {
          const overflow = next.length - MAX_VISIBLE;
          const dropped = next.splice(0, overflow);
          for (const d of dropped) {
            const t = timersRef.current.get(d.id);
            if (t) {
              clearTimeout(t);
              timersRef.current.delete(d.id);
            }
          }
        }
        return next;
      });
      if (durationMs > 0) {
        const timer = setTimeout(() => dismiss(id), durationMs);
        timersRef.current.set(id, timer);
      }
    },
    [dismiss],
  );

  const pushSuccess = useCallback((message: string) => push(message, 'success'), [push]);
  const pushError = useCallback((message: string) => push(message, 'error'), [push]);

  // Clear all pending timers on unmount.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const t of timers.values()) clearTimeout(t);
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({ push, pushSuccess, pushError, dismiss }),
    [push, pushSuccess, pushError, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

interface ToastStackProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

function ToastStack({ toasts, onDismiss }: ToastStackProps) {
  if (typeof document === 'undefined') return null;
  if (toasts.length === 0) return null;
  return createPortal(
    <div className="toast-stack" role="region" aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => (
        <Toast key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    document.body,
  );
}

interface ToastProps {
  toast: ToastItem;
  onDismiss: () => void;
}

function Toast({ toast, onDismiss }: ToastProps) {
  return (
    <button
      type="button"
      className={`toast toast-${toast.type}`}
      onClick={onDismiss}
      aria-label={`Dismiss notification: ${toast.message}`}
    >
      <span className="toast-message">{toast.message}</span>
    </button>
  );
}

export default ToastProvider;
